import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Tooltip, Typography } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import CityManagerDialog from '../CityManagerDialog';
import { EditEmptyState } from '../edit/EditCommon';
import { getTodayStr } from '../edit/editCalc';
import { MIN_REPORT_DATE } from '../summary/summaryApi';
import { ACTION_BUTTON_SX, EXCEL_BUTTON_SX } from '../summary/summaryStyles';
import type { MatchStats } from '../delivery/deliveryCalc';
import { ReportSecurityWrapper } from '../ReportSecurityWrapper';
import ReportHistoryButton from '../history/ReportHistoryButton';
import { usePermissions } from '../../../../utils/usePermissions';
import DeliveryTable from './DeliveryTable';
import FileSlot from './FileSlot';
import { useCompare } from './useCompare';

const BORDER = '1px solid #e2e8f0';

interface Notice {
  text: string;
  warn: boolean;
}

const fmt = (n: number) => n.toLocaleString('ru-RU');

/** Короткие пояснения под панелью: как распределились заказы и что требует внимания (не больше трех строк) */
function describeStats(stats: MatchStats, hasArchive: boolean): Notice[] {
  const parts = hasArchive
    ? [
      `по складу КД: ${fmt(stats.warehouse)}`,
      `по адресу доставки: ${fmt(stats.archiveAddress)}`,
      `по адресу выписки: ${fmt(stats.salesAddress)}`,
      `без аптеки: ${fmt(stats.unassigned)}`
    ]
    : [`по адресу выписки: ${fmt(stats.salesAddress)}`, `без аптеки: ${fmt(stats.unassigned)}`];

  const notices: Notice[] = [{ text: `Заказов в выписке: ${fmt(stats.processed)} · ${parts.join(' · ')}`, warn: false }];
  if (!hasArchive) {
    notices.push({ text: 'Archive Orders не загружен: аптека определяется только по адресу из выписки', warn: false });
  } else if (stats.notInArchive > 0) {
    notices.push({ text: `Не найдено в Archive Orders: ${fmt(stats.notInArchive)}`, warn: true });
  }
  if (stats.badAmounts > 0) {
    notices.push({ text: `Сумма не распознана в строках: ${fmt(stats.badAmounts)} (учтены как 0)`, warn: true });
  }
  return notices;
}

const Compare: React.FC = () => {
  const { hasPerm } = usePermissions();
  const canProtectCopy = hasPerm('reports.delivery.protect_copy');
  const canManageTerminals = hasPerm('reports.delivery.manage_terminals');

  const {
    terminals,
    terminalsError,
    retryTerminals,
    handleTerminalsChanged,
    files,
    selectFile,
    clearFile,
    date,
    setDate,
    day,
    retryDay,
    summary,
    comparison,
    canDownload,
    exporting,
    download
  } = useCompare();

  const [cityManagerOpen, setCityManagerOpen] = useState(false);

  const openCityManager = canManageTerminals ? (
    <Button variant="outlined" onClick={() => setCityManagerOpen(true)} sx={ACTION_BUTTON_SX}>
      Управление городами
    </Button>
  ) : undefined;

  const renderContent = () => {
    if (terminalsError) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить данные о городах"
          text={terminalsError}
          action={<Button variant="outlined" onClick={retryTerminals} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!terminals) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (terminals.length === 0) {
      return (
        <EditEmptyState
          icon={<LocationCityOutlinedIcon />}
          title="Города не настроены"
          text="Сверку не с чем проводить: добавьте города с терминалами и привяжите их к аптекам."
          action={openCityManager}
        />
      );
    }
    if (!summary && !day.data) {
      if (day.loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        );
      }
      return (
        <EditEmptyState
          icon={<CompareArrowsIcon />}
          title="Нет данных для сравнения"
          text="Загрузите выписку Sales Report и выберите дату, чтобы сравнить суммы с доставками аптек."
        />
      );
    }
    return (
      <DeliveryTable
        rows={comparison.rows}
        totals={comparison.totals}
        stale={day.loading || files.sales.loading || files.archive.loading}
      />
    );
  };

  const notices = summary ? describeStats(summary.stats, !!files.archive.table) : [];

  return (
    <ReportSecurityWrapper protectCopy={canProtectCopy}>
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: 3,
            p: 2.5,
            mb: 2,
            border: BORDER,
            borderRadius: '12px'
          }}
        >
          <FileSlot
            label="Sales Report"
            note="выписка"
            fileName={files.sales.name}
            rowCount={files.sales.table ? files.sales.table.rows.length : null}
            loading={files.sales.loading}
            error={files.sales.error}
            onSelect={(file) => selectFile('sales', file)}
            onClear={() => clearFile('sales')}
          />
          <FileSlot
            label="Archive Orders"
            note="определяет аптеку по складу КД"
            fileName={files.archive.name}
            rowCount={files.archive.table ? files.archive.table.rows.length : null}
            loading={files.archive.loading}
            error={files.archive.error}
            onSelect={(file) => selectFile('archive', file)}
            onClear={() => clearFile('archive')}
          />

          <TextField
            size="small"
            label="Дата отчета"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: MIN_REPORT_DATE, max: getTodayStr() }}
            sx={{ minWidth: 165, mt: 2.75 }}
          />

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', ml: 'auto', mt: 2.75 }}>
            {openCityManager}
            <ReportHistoryButton screen="delivery" />
            <Tooltip title={canDownload || exporting ? '' : 'Загрузите оба файла, чтобы скачать обработанную выписку'} arrow>
              <span>
                <Button
                  variant="contained"
                  disabled={!canDownload}
                  onClick={download}
                  startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadOutlinedIcon />}
                  sx={EXCEL_BUTTON_SX}
                >
                  Скачать Excel
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>

        {notices.length > 0 && (
          <Box sx={{ mb: 2, px: 0.5 }}>
            {notices.map((notice) => (
              <Typography
                key={notice.text}
                sx={{ fontSize: '0.82rem', lineHeight: 1.6, color: notice.warn ? '#b45309' : '#64748b' }}
              >
                {notice.text}
              </Typography>
            ))}
          </Box>
        )}

        {day.error && (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: '10px' }}
            action={<Button color="inherit" size="small" onClick={retryDay}>Повторить</Button>}
          >
            {day.error}
          </Alert>
        )}

        {renderContent()}

        <CityManagerDialog
          open={cityManagerOpen}
          onClose={() => setCityManagerOpen(false)}
          onChanged={handleTerminalsChanged}
        />
      </Box>
    </ReportSecurityWrapper>
  );
};

export default Compare;
